import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import type {
  ProgramSwitcherSelectionsV1 as ProgramSwitcherSelectionsV1Type,
  ProgramSwitcherSelectionsV1Variables,
} from 'bundles/course-v2/components/day-one-onboarding-modal/hooks/useEnableDayOneOnboarding/__generated__/ProgramSwitcherSelectionsV1';
import programSwitcherSelectionsQuery from 'bundles/course-v2/components/day-one-onboarding-modal/hooks/useEnableDayOneOnboarding/programSwitcherSelectionsQuery';
import { useNaptime } from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

const useSwitchToCurrentProgram = () => {
  const { naptime } = useNaptime(() => ({}));
  const program = useProgram();
  const programMembership = useProgramMembership();

  useQuery<ProgramSwitcherSelectionsV1Type, ProgramSwitcherSelectionsV1Variables>(programSwitcherSelectionsQuery, {
    variables: { userId: String(getUser()?.id) },
    skip: !isAuthenticatedUser() || !programMembership.isProgramMember,
    ssr: true,
    errorPolicy: 'all', // Dont want to block render in case of an error.
    context: { clientName: 'gatewayGql' },
    onCompleted: async (data) => {
      const switcherProgramId = data?.ProgramSwitcherSelectionsV1Resource.get?.programId;
      if (!program?.id || program.id === switcherProgramId) {
        // If the program is already selected, return
        return;
      }
      try {
        await naptime.executeMutation(
          ProgramSwitcherSelectionsV1.update(getUser().id, {
            selectionType: 'PROGRAM',
            programId: program.id,
          })
        );
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: '[Learner Flow Optimization] Error updating program switcher selection',
          },
        });
      }
    },
    onError: (errorObj) => {
      Sentry.captureException(errorObj, {
        extra: { message: '[Learner Flow Optimization] Error fetching program switcher in useSelectedOrg.' },
      });
    },
  });
};

export default useSwitchToCurrentProgram;
